import { ParsedUrlQuery } from 'querystring';

import { GetStaticProps } from 'next';
import sanitize from 'sanitize-html';

import { Layout } from '@/components/Layout';
import ArboterumPlants from '@/components/sections/ArboterumPlants';
import { PhoneFooter } from '@/components/sections/PhoneFooter';
import { WhatToSee } from '@/components/sections/WhatToSee';
import { ContentWithSidebar } from '@/components/shared/ContentWithSidebar';
import { EmptyContent } from '@/components/shared/EmptyContent';
import { ImageSlider } from '@/components/shared/ImageSlider';
import { PageHeading } from '@/components/shared/PageHeading';
import api from '@/helpers/api';
import { sortSiteMenu } from '@/helpers/common';
import getPageBanner from '@/helpers/getPageBanner';
import { getI18nPaths, getI18nProps } from '@/helpers/i18n';
import { usePageContent } from '@/hooks/usePageContent';
import { usePageSeo } from '@/hooks/usePageSeo';
import { COMMON_LOCALE_PART } from '@/locale';
import { IPage, Locale, SiteMenuItem, IPageDetailsProps, IPlantDetails } from '@/types';
import { useLocale } from '@/hooks/useLocale';

const DefaultPage = ({ params, siteMenu, pageDetails }: IPageDetailsProps) => {
  const { seoOptions } = usePageSeo(pageDetails);
  const { ContentComponent } = usePageContent(pageDetails);
  const { currentLocale: locale } = useLocale();
  const renderContent = () => {
    if (!pageDetails) return <EmptyContent />;

    const { name, plants, gallery, show_banner, slug } = pageDetails;

    return (
      <>
        <ContentWithSidebar siteMenu={siteMenu}>
          <PageHeading>{name}</PageHeading>

          {ContentComponent}

          {!!gallery.length && (
            <div className="mb-8 mt-8 rounded-lg bg-white p-4 shadow-lg">
              <ImageSlider name={name} sliderImages={gallery} vertical />
            </div>
          )}

          {!!plants.length && locale === 'ua' && <ArboterumPlants plants={plants} />}

          {slug === 'seasons' && (
            <div className="[&>*>h2]:hidden [&>*>p]:hidden [&>*]:m-0 [&>*]:w-full">
              <hr className="pb-6" />
              <WhatToSee siteMenu={siteMenu} />
            </div>
          )}
        </ContentWithSidebar>

        {show_banner && <PhoneFooter />}
      </>
    );
  };

  return (
    <>
      <Layout siteMenu={siteMenu} seoOptions={seoOptions} banner={getPageBanner(pageDetails)}>
        {renderContent()}
      </Layout>
    </>
  );
};

interface IParams extends ParsedUrlQuery {
  type: string;
}

export async function getStaticPaths() {
  const request = api({});
  const pages = await request<IPage[]>('pages/');

  const pagesMapped = pages.data
    .filter((page) => !['interactive-map', 'plan-your-visit', 'faq', 'our-partners'].includes(page.slug))
    .map((page) => ({
      params: {
        type: page.slug,
      },
    }));

  const paths = getI18nPaths()
    .map(({ params: paramsLocale }) => {
      return pagesMapped.map(({ params: paramsPaths }) => ({
        params: { ...paramsPaths, ...paramsLocale },
      }));
    })
    .flat();

  return {
    paths,
    fallback: false,
  };
}

interface IParams extends ParsedUrlQuery {
  locale: Locale;
}

interface DefaultPageStaticProps extends GetStaticProps {
  locale: Locale;
  params: IParams;
}

export const getStaticProps = async ({ params }: DefaultPageStaticProps) => {
  const { locale } = params;
  const request = api({ locale: locale });

  const { type } = params;

  const responses = await Promise.allSettled([
    request<SiteMenuItem[]>('site-menu/'),
    request<IPage>(`pages/detail/${type}`),
  ]);

  const siteMenu = responses[0].status === 'fulfilled' ? responses[0].value.data : undefined;
  const pageDetails = responses[1].status === 'fulfilled' ? responses[1].value.data : undefined;

  return {
    props: {
      ...(await getI18nProps(locale, [COMMON_LOCALE_PART])),
      siteMenu: sortSiteMenu(locale, siteMenu as SiteMenuItem[]),
      pageDetails: preparePageDetails(pageDetails),
    },
  };
};

export default DefaultPage;

function preparePageDetails(pageDetails: IPage | undefined) {
  // TODO: we remove other images except is_main cause we render only is_main, by that step reduced content size
  function plantsImages(plants: Partial<IPlantDetails>[]) {
    return plants?.map((plant) => ({
      ...plant,
      images: plant?.images?.filter((image) => image.is_main),
    }));
  }

  const plants = pageDetails?.plants ? { plants: plantsImages(pageDetails?.plants) } : {};
  return {
    ...pageDetails,
    page_body: sanitize(pageDetails?.page_body ?? '', {
      allowedTags: sanitize.defaults.allowedTags.concat(['img']),
      allowedAttributes: {
        figure: ['style', 'class'],
        img: ['src', 'alt', 'class'],
        a: ['href', 'target', 'rel', 'class', 'style'],
        p: ['class', 'style'],
        span: ['class', 'style'],
        h1: ['class', 'style'],
        h2: ['class', 'style'],
        h3: ['class', 'style'],
        h4: ['class', 'style'],
        h5: ['class', 'style'],
        table: ['class', 'style'],
      },
      allowedSchemes: ['data', 'http', 'https'],
      allowedClasses: {
        table: ['table'],
        figure: ['image', 'image-style-side', 'table'],
      },
    }),
    ...plants,
  };
}
